import React, { Fragment, useEffect, useState } from "react";
import { ReactComponent as SearchIcon } from "assets/images/common/svgs/search.svg";
import { siteRoutes } from "utils/helpers/enums/routes.enum";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { EditPenSvg } from "assets/images/common/svgs";
import useTeamMembers from "containers/team-management/useHooks";
import { useDebounce } from "hooks/useDebounce";
import { confirmationPopup } from "utils/helpers/common/alert-service";
import { RoleFilterDTO } from "utils/helpers/models/role-management/role-filter.dto";
import DataNotFound from "components/particles/table/data-not-found";
import Pagination from "components/particles/table/pagination";
import useStore from "hooks/useStore";
import { sitePermissions } from "utils/helpers/enums/permissions.enum";

const RoleManagementListing: React.FC = () => {
  const { hasPermission } = useStore();

  const navigate = useNavigate();
  const [data, setData] = useState<any[]>([]);
  const { getRoles, toggleRole } = useTeamMembers();
  const { isLoading } = useSelector((state: any) => state.sharedReducer);
  const [filters, setFilters] = useState<RoleFilterDTO>(new RoleFilterDTO());

  const buildUpdatedFilters = (
    updates: Partial<RoleFilterDTO> = {},
    resetPage: boolean = false
  ) => {
    const updatedFilters = { ...filters, ...updates };
    if (resetPage) {
      updatedFilters.page = 1;
    }
    return updatedFilters;
  };

  const [totalElements, setTotalElements] = useState<number>(0);
  const debounceSearch = useDebounce(filters.roleName, 1500);
  const columns: string[] = ["Name", "Status", "Action"];

  const onPageChange = (pageInfo: { selected: number; limit: number }) => {
    const { selected: page, limit } = pageInfo;
    const updatedFilters = buildUpdatedFilters(
      { limit: limit, page: page + 1 },
      false
    );
    setFilters(updatedFilters);
    getRolesData(updatedFilters);
  };

  const getRolesData = (filters: RoleFilterDTO) => {
    const filtersToSend: any = {};
    for (let key in filters) {
      if (filters[key] !== undefined && filters[key] !== null && filters[key] !== "") {
        filtersToSend[key] = filters[key];
      }
    }
    const queryParams = {
      ...filtersToSend,
      page: filters.page - 1,
    };
    getRoles(setData, queryParams, setFilters, setTotalElements);
  };

  useEffect(() => {
    getRolesData(filters);
  }, [debounceSearch]);

  // Initial load on component mount
  useEffect(() => {
    getRolesData(filters);
  }, []);

  const handleChangeFilter = (event: any) => {
    const {
      target: { name, value },
    } = event;
    const trimmedValue = typeof value === "string" ? value.trim() : value;
    const updatedFilters = buildUpdatedFilters(
      { [name]: trimmedValue },
      true
    );
    setFilters(updatedFilters);
    if (name === "roleName" && trimmedValue === "") {
      getRolesData(updatedFilters);
    }
  };

  const handleSearch = (event: any) => {
    const { key } = event;
    if (key === "Enter") {
      const updatedFilters = buildUpdatedFilters({}, true);
      setFilters(updatedFilters);
      getRolesData(updatedFilters);
    }
  };

  const handleChangeStatus = async (user: any, index: number) => {
    const { id: id, status } = user;

    const result = await confirmationPopup(
      `Are you sure you want to ${
        status ? "Deactivate" : "Activate"
      } this Role?`
    );

    if (result.isConfirmed) {
      if (status) {
        toggleRole(id);
      } else {
        toggleRole(id);
      }
      user.status = !user.status;
      data[index] = user;
      setData([...data]);
    }
  };

  const goToEditPage = (roleId: number) => {
    navigate(`${siteRoutes.editRole}?id=${roleId}`);
  };

  return (
    <div className="">
      <h1 className="tex-2xl sm:text-[32px] text-primaryText font-bold">
        Role Management
      </h1>
      <div className="flex justify-between flex-wrap items-center gap-5 my-6">
        <div className="">
          {hasPermission(sitePermissions.roleManagementCreate) && (
            <button
              onClick={() => {
                navigate(siteRoutes.createRole);
              }}
              className="px-4 py-2 text-sm bg-goldenYellowBg text-white  rounded-lg font-semibold flex gap-1 items-center"
            >
              Create New Role
            </button>
          )}
        </div>
        <div className="w-full sm:w-[388px] relative">
          <SearchIcon className="absolute left-4 top-1/2 transform -translate-y-1/2 text-primaryText text-lg" />
          <input
            type="search"
            placeholder="Search by Name"
            value={filters.roleName}
            onChange={handleChangeFilter}
            onKeyUp={handleSearch}
            name="roleName"
            className="w-full search-input pl-10 p-2 border-[0.6px] border-borderLightGray rounded-full focus:outline-none bg-coolLightGrayBg"
          />
        </div>
      </div>

      <div className="w-full flex flex-col gap-4">
        <div className="bg-white rounded-t-lg  border border-mediumGrayBorder overflow-x-auto">
          <table className="min-w-full divide-y divide-mediumGrayBorder">
            {/* Table Header */}
            <thead className="bg-white">
              <tr className="text-left">
                {columns.map((column, index) => {
                  return (
                    <th
                      className="px-4 py-4 text-sm font-bold text-left text-primaryText whitespace-nowrap"
                      key={index}
                    >
                      {column}
                    </th>
                  );
                })}
              </tr>
            </thead>

            {/* Table Body */}
            <tbody className="bg-white divide-y divide-mediumGrayBorder">
              {data.map((item: any, index) => (
                <tr key={index} className="hover:bg-gray-50 ">
                  <td className="px-4 py-4 text-sm  text-primaryText font-semibold whitespace-nowrap">
                    {item.name}
                  </td>

                  <td className="px-4 py-4 text-sm font-semibold whitespace-nowrap">
                    <label className="switch-field">
                      <input
                        type="checkbox"
                        checked={item?.status}
                        onChange={() => handleChangeStatus(item, index)}
                      />
                      <span className="slider"></span>
                    </label>
                  </td>

                  <td className="px-4 py-4 text-left whitespace-nowrap w-[124px]">
                    <div>
                      {hasPermission(sitePermissions.roleManagementUpdate) ? (
                        <button onClick={() => goToEditPage(item.id)}>
                          <EditPenSvg className="w-[28px] h-[28px]" />
                        </button>
                      ) : (
                        <span className="cursor-not-allowed">
                          <EditPenSvg className="w-[28px] h-[28px]" />
                        </span>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <Fragment>
          <DataNotFound show={!isLoading && !data.length} />
          {totalElements > 0 && (
            <Pagination
              count={totalElements}
              page={filters.page}
              limit={filters.limit}
              onPageChange={onPageChange}
            />
          )}
        </Fragment>
      </div>
    </div>
  );
};

export default RoleManagementListing;
